import { useState, useEffect, useCallback } from 'react';
import apiService from '../../../services/ApiService';
import { useAppStore } from '../../../shared/stores/useAppStore';
import { INITIAL_SALESFORCE_FORM } from '../constants';
import {
    ViewMode,
    ErpConnection,
    ConnectionDetails,
    SalesforceFormData,
    SalesforceConnectionDetails,
} from '../types';

export function useErpConnections(open: boolean, organizationId: number) {
    const [connections, setConnections] = useState<ErpConnection[]>([]);
    const [loading, setLoading] = useState(false);
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [selectedConnection, setSelectedConnection] = useState<ConnectionDetails | null>(null);
    const [selectedConnectionType, setSelectedConnectionType] = useState('');
    const [newConnectionType, setNewConnectionType] = useState('');
    const [createLoading, setCreateLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [testResult, setTestResult] = useState<{ status: boolean } | null>(null);
    const [isNewlyCreated, setIsNewlyCreated] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [privateKeyInputMode, setPrivateKeyInputMode] = useState<'text' | 'file'>('file');
    const [salesforceForm, setSalesforceForm] = useState<SalesforceFormData>(INITIAL_SALESFORCE_FORM);

    const resetState = useCallback(() => {
        setViewMode('list');
        setSelectedConnection(null);
        setSelectedConnectionType('');
        setNewConnectionType('');
        setIsEditing(false);
        setSaveError(null);
        setSaveSuccess(false);
        setPrivateKeyInputMode('file');
        setTestResult(null);
        setIsNewlyCreated(false);
        setShowDeleteConfirm(false);
    }, []);

    const loadConnections = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiService.getErpConnections();
            if (response) {
                const data = await response.json();
                setConnections(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Failed to load ERP connections:', error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (open) {
            loadConnections();
        }
    }, [open, loadConnections]);

    const handleViewDetails = useCallback(async (connection: ErpConnection) => {
        setLoading(true);
        setSaveError(null);
        setSaveSuccess(false);
        setIsEditing(false);
        setIsNewlyCreated(false);

        try {
            let response;
            if (connection.type === 'salesforce') {
                response = await apiService.getSalesforceConnection(connection.id);
            }

            if (response) {
                const data = await response.json();
                setSelectedConnection(data);
                setSelectedConnectionType(connection.type);
                setViewMode('details');

                if (connection.type === 'salesforce') {
                    const conn = data as SalesforceConnectionDetails;
                    setSalesforceForm({
                        name: conn.name || '',
                        sf_instance_url: conn.sf_instance_url || '',
                        sf_client_id: conn.sf_client_id || '',
                        sf_username: conn.sf_username || '',
                        sf_domain: conn.sf_domain || '',
                        sf_private_key: '',
                    });
                }
            }
        } catch (error) {
            console.error('Failed to load connection details:', error);
        }
        setLoading(false);
    }, []);

    const handleAddConnection = useCallback(() => {
        setViewMode('add');
        setNewConnectionType('');
        setSalesforceForm(INITIAL_SALESFORCE_FORM);
        setSaveError(null);
        setSaveSuccess(false);
    }, []);

    const handleBackToList = useCallback(() => {
        resetState();
    }, [resetState]);

    const handleCreateConnection = useCallback(async () => {
        if (!newConnectionType) return;

        setCreateLoading(true);
        setSaveError(null);

        try {
            let response;

            if (newConnectionType === 'salesforce') {
                response = await apiService.createSalesforceConnection(
                    organizationId,
                    newConnectionType,
                    salesforceForm.name,
                    salesforceForm.sf_instance_url,
                    salesforceForm.sf_client_id,
                    salesforceForm.sf_username,
                    salesforceForm.sf_domain,
                    salesforceForm.sf_private_key
                );
            }

            if (response && response.ok) {
                const createdConnection = await response.json();
                await loadConnections();

                useAppStore.getState().setHasErpConnections(true);

                setSelectedConnection(createdConnection);
                setSelectedConnectionType(newConnectionType);
                setSalesforceForm(prev => ({ ...prev, sf_private_key: '' }));
                setNewConnectionType('');
                setViewMode('details');
                setIsNewlyCreated(true);
                setSaveSuccess(true);
            } else if (response) {
                const errorData = await response.json();
                setSaveError(errorData?.detail || 'Failed to create connection');
            }
        } catch (error) {
            console.error('Failed to create connection:', error);
            setSaveError('Failed to create connection');
        }
        setCreateLoading(false);
    }, [newConnectionType, organizationId, salesforceForm, loadConnections]);

    const handleSaveConnection = useCallback(async () => {
        if (!selectedConnection) return;

        setSaveLoading(true);
        setSaveError(null);

        try {
            let response;
            if (selectedConnectionType === 'salesforce') {
                response = await apiService.updateSalesforceConnection(
                    selectedConnection.id,
                    salesforceForm.name,
                    salesforceForm.sf_instance_url,
                    salesforceForm.sf_client_id,
                    salesforceForm.sf_username,
                    salesforceForm.sf_domain,
                    salesforceForm.sf_private_key
                );
            }

            if (response && response.ok) {
                const updatedData = await response.json();
                setSelectedConnection(updatedData);
                setIsEditing(false);
                setIsNewlyCreated(false);
                setSaveSuccess(true);
                setSalesforceForm(prev => ({ ...prev, sf_private_key: '' }));
            } else {
                const errorData = await response?.json();
                setSaveError(errorData?.detail || 'Failed to save connection');
            }
        } catch (error) {
            console.error('Failed to save connection:', error);
            setSaveError('Failed to save connection');
        }
        setSaveLoading(false);
    }, [selectedConnection, selectedConnectionType, salesforceForm]);

    const handleStartEditing = useCallback(() => {
        if (!selectedConnection) return;

        const conn = selectedConnection as SalesforceConnectionDetails;
        if (selectedConnectionType === 'salesforce') {
            setSalesforceForm({
                name: conn.name || '',
                sf_instance_url: conn.sf_instance_url || '',
                sf_client_id: conn.sf_client_id || '',
                sf_username: conn.sf_username || '',
                sf_domain: conn.sf_domain || '',
                sf_private_key: '',
            });
        }
        setIsEditing(true);
    }, [selectedConnection, selectedConnectionType]);

    const handleTestConnection = useCallback(async () => {
        if (!selectedConnection) return;

        setTestLoading(true);
        setTestResult(null);

        try {
            let response;
            if (selectedConnectionType === 'salesforce') {
                response = await apiService.testSalesforceConnection(selectedConnection.id);
            }

            if (response) {
                const data = await response.json();
                setTestResult(data);
            }
        } catch (error) {
            console.error('Failed to test connection:', error);
            setTestResult({ status: false });
        }
        setTestLoading(false);
    }, [selectedConnection, selectedConnectionType]);

    const handleDeleteConnection = useCallback(async () => {
        if (!selectedConnection) return;

        setDeleteLoading(true);

        try {
            let response;
            if (selectedConnectionType === 'salesforce') {
                response = await apiService.deleteSalesforceConnection(selectedConnection.id);
            }

            if (response && response.ok) {
                await loadConnections();

                const updatedResponse = await apiService.getErpConnections();
                if (updatedResponse && updatedResponse.ok) {
                    const data = await updatedResponse.json();
                    useAppStore.getState().setHasErpConnections(Array.isArray(data) && data.length > 0);
                }

                setShowDeleteConfirm(false);
                handleBackToList();
            } else {
                const errorData = await response?.json();
                setSaveError(errorData?.detail || 'Failed to delete connection');
            }
        } catch (error) {
            console.error('Failed to delete connection:', error);
            setSaveError('Failed to delete connection');
        }
        setDeleteLoading(false);
    }, [selectedConnection, selectedConnectionType, loadConnections, handleBackToList]);

    return {
        // State
        connections,
        loading,
        viewMode,
        selectedConnection,
        selectedConnectionType,
        newConnectionType,
        createLoading,
        isEditing,
        saveLoading,
        saveError,
        saveSuccess,
        testLoading,
        testResult,
        isNewlyCreated,
        deleteLoading,
        showDeleteConfirm,
        privateKeyInputMode,
        salesforceForm,

        // Setters
        setNewConnectionType,
        setIsEditing,
        setSaveError,
        setSaveSuccess,
        setTestResult,
        setShowDeleteConfirm,
        setPrivateKeyInputMode,
        setSalesforceForm,

        // Actions
        resetState,
        loadConnections,
        handleViewDetails,
        handleAddConnection,
        handleBackToList,
        handleCreateConnection,
        handleSaveConnection,
        handleStartEditing,
        handleTestConnection,
        handleDeleteConnection,
    };
}
